import { VideoStreamSupportedMediaType } from './types';
import {
    HlsController,
    IVideoStreamController,
    MotionJpegController,
    RtspController,
    VideoCaptureController,
    VideoStreamController,
    YoutubeController,
} from './VideoStreamControllers';
import { Log } from '../../AuxillaryService/Log';
import {
    VideoStreamControllerURLSourceConfiguration,
    VLCVideoStreamControllerConfiguration,
    ZodVideoStreamControllerURLSourceConfiguration,
    ZodVLCVideoStreamControllerConfiguration,
} from './VideoStreamControllers/types';
import { assertNever } from '../../Utility/assert';
import { isNullish } from '../../Utility/Nullish';
import { VideoStreamUnsupportedVLCController } from './VideoStreamControllers/VideoStreamUnsupportedVLCController';
import { VideoStreamExternalVLCController } from './VideoStreamControllers/VideoStreamExternalVLCController';
import { VideoStreamLibVLCController } from './VideoStreamControllers/VideoStreamLibVLCController';

// WARNING: `REACH` should not be called outside of a `Platform` module.
// TODO: move `REACH` calls into `ReachPlayer`.

/**
 * @throws {@link AssertionError} if `mediaType` is unknown
 */
export function createVideoStreamController(
    wrapperElement: HTMLDivElement,
    mediaType: VideoStreamSupportedMediaType,
    config?: VideoStreamControllerURLSourceConfiguration | VLCVideoStreamControllerConfiguration
): VideoStreamController | IVideoStreamController {
    let controller: VideoStreamController | IVideoStreamController | undefined;
    let parsedConfig;

    switch (mediaType) {
        case VideoStreamSupportedMediaType.MJPEG:
            parsedConfig = ZodVideoStreamControllerURLSourceConfiguration.parse(config);
            controller = new MotionJpegController(wrapperElement, parsedConfig.url);
            break;

        case VideoStreamSupportedMediaType.RTP_BRIGHTSIGN:
            parsedConfig = ZodVideoStreamControllerURLSourceConfiguration.parse(config);
            controller = new RtspController(wrapperElement, parsedConfig.url);
            break;

        case VideoStreamSupportedMediaType.HLS:
        case VideoStreamSupportedMediaType.REACH:
            parsedConfig = ZodVideoStreamControllerURLSourceConfiguration.parse(config);
            controller = new HlsController(wrapperElement, parsedConfig.url);
            break;

        case VideoStreamSupportedMediaType.YOUTUBE_LIVE:
            parsedConfig = ZodVideoStreamControllerURLSourceConfiguration.parse(config);
            controller = new YoutubeController(wrapperElement, parsedConfig.url);
            break;

        case VideoStreamSupportedMediaType.RTP_WINDOWS:
        case VideoStreamSupportedMediaType.RTMP_WINDOWS:
        case VideoStreamSupportedMediaType.HDMI_WINDOWS:
        case VideoStreamSupportedMediaType.VLC:
            parsedConfig = ZodVLCVideoStreamControllerConfiguration.parse(config);

            if (isNullish(REACH?.VLC)) {
                controller = new VideoStreamUnsupportedVLCController();
            } else if ('RegisterVlcControlListener' in REACH.VLC) {
                controller = new VideoStreamLibVLCController(parsedConfig, REACH.VLC);
            } else {
                controller = new VideoStreamExternalVLCController(parsedConfig, REACH.VLC);
            }
            break;

        case VideoStreamSupportedMediaType.HDMI_BRIGHTSIGN:
            controller = new VideoCaptureController(wrapperElement);
            break;

        default: {
            const errorMsg = `Video Stream media type ${mediaType} is not supported.`;
            Log.Error(errorMsg, null, __fileline, mediaType);
            assertNever(mediaType, errorMsg);
        }
    }

    return controller;
}
